/**
 * Finestra modale.
 *
 * Chiusura con Esc e con il clic sul velo, fuoco riportato sul contenuto
 * all'apertura e scorrimento del corpo bloccato: sono le tre cose che una
 * modale scritta a mano di solito dimentica e che rendono la tastiera
 * inutilizzabile.
 */

import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { IconaChiudi } from "./Icone";

export function Modale({
  titolo,
  aperta,
  onChiudi,
  pedice,
  children,
}: {
  titolo: string;
  aperta: boolean;
  onChiudi: () => void;
  /** Bottoni di conferma e annullamento. */
  pedice?: ReactNode;
  children: ReactNode;
}) {
  const contenitore = useRef<HTMLDivElement>(null);

  // `onChiudi` è quasi sempre una funzione anonima scritta sul posto dal
  // chiamante, quindi cambia identità a ogni render. Tenerla fra le dipendenze
  // dell'effetto lo faceva rieseguire a ogni battuta di tasto, e
  // `contenitore.focus()` toglieva il fuoco al campo di testo appena scritto: si
  // riusciva a digitare **un carattere solo**. Il ref rompe quel legame senza
  // far leggere all'effetto una versione vecchia della funzione.
  const chiudiRef = useRef(onChiudi);
  useEffect(() => {
    chiudiRef.current = onChiudi;
  });

  useEffect(() => {
    if (!aperta) return;

    const allaTastiera = (evento: KeyboardEvent) => {
      if (evento.key === "Escape") chiudiRef.current();
    };
    document.addEventListener("keydown", allaTastiera);

    const scorrimentoPrecedente = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Il fuoco si sposta **una volta sola**, all'apertura: da lì in poi lo
    // governa chi naviga.
    contenitore.current?.focus();

    return () => {
      document.removeEventListener("keydown", allaTastiera);
      document.body.style.overflow = scorrimentoPrecedente;
    };
  }, [aperta]);

  if (!aperta) return null;

  return (
    <div
      className="velo"
      onMouseDown={(evento) => {
        if (evento.target === evento.currentTarget) onChiudi();
      }}
    >
      <div
        className="modale"
        role="dialog"
        aria-modal="true"
        aria-label={titolo}
        tabIndex={-1}
        ref={contenitore}
      >
        <header className="modale__testata">
          <h2 className="modale__titolo">{titolo}</h2>
          <button
            type="button"
            className="bottone bottone--fantasma bottone--piccolo bottone--icona"
            onClick={onChiudi}
            aria-label="Chiudi la finestra"
          >
            <IconaChiudi />
          </button>
        </header>
        <div className="modale__corpo">{children}</div>
        {pedice ? <footer className="modale__pedice">{pedice}</footer> : null}
      </div>
    </div>
  );
}
